import apiClient from './client';
import { Pagination, ApiResponse, User, Repository, Project } from '@/types';

/**
 * Issue severity
 */
export type IssueSeverity = 'critical' | 'major' | 'minor' | 'suggestion';

/**
 * Issue category
 */
export type IssueCategory = 'bug' | 'security' | 'performance' | 'style' | 'maintainability' | 'other';

/**
 * Review issue
 */
export interface ReviewIssue {
  severity: IssueSeverity;
  category: IssueCategory;
  file?: string;
  line?: number;
  message: string;
  suggestion?: string;
}

/**
 * File review
 */
export interface FileReview {
  path: string;
  score: number;
  issues: ReviewIssue[];
  summary: string;
}

/**
 * Code review
 */
export interface CodeReview {
  id: string;
  commitSha: string;
  commitId?: {
    sha: string;
    shortSha: string;
    title: string;
    webUrl: string;
  };
  repoId: Repository;
  projectId?: Project;
  userId?: User;
  gitlabProjectId: number;
  gitlabMrIid?: number;
  summary: string;
  overallScore: number;
  issues: ReviewIssue[];
  positives: string[];
  recommendations: string[];
  estimatedComplexity: 'low' | 'medium' | 'high';
  fileReviews: FileReview[];
  aiProvider?: string;
  aiModel?: string;
  tokensUsed: number;
  costUsd: number;
  processingTimeMs: number;
  developerNotified: boolean;
  developerNotifiedAt?: string;
  gitlabCommentPosted: boolean;
  status: 'pending' | 'completed' | 'failed' | 'skipped';
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Review query parameters
 */
export interface ReviewQueryParams {
  page?: number;
  limit?: number;
  status?: string;
  userId?: string;
  repoId?: string;
  projectId?: string;
  minScore?: number;
  maxScore?: number;
  hasCritical?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Review list response
 */
export interface ReviewListResponse {
  data: CodeReview[];
  pagination: Pagination;
}

/**
 * Review stats
 */
export interface ReviewStats {
  totalReviews: number;
  averageScore: number;
  totalIssues: number;
  criticalIssues: number;
  majorIssues: number;
  minorIssues: number;
  avgProcessingTime: number;
  totalCost: number;
  scoreDistribution: Array<{ range: string; count: number }>;
  statusCounts: Record<string, number>;
}

/**
 * Get reviews with filters
 */
export async function getReviews(params?: ReviewQueryParams): Promise<ReviewListResponse> {
  const response = await apiClient.get<ApiResponse<ReviewListResponse>>('/reviews', { params });
  return response.data.data as ReviewListResponse;
}

/**
 * Get review statistics
 */
export async function getReviewStats(params?: {
  userId?: string;
  repoId?: string;
  projectId?: string;
  days?: number;
}): Promise<ReviewStats> {
  const response = await apiClient.get<ApiResponse<ReviewStats>>('/reviews/stats', { params });
  return response.data.data as ReviewStats;
}

/**
 * Get a single review by ID
 */
export async function getReview(id: string): Promise<CodeReview> {
  const response = await apiClient.get<ApiResponse<CodeReview>>(`/reviews/${id}`);
  return response.data.data as CodeReview;
}

/**
 * Get review by commit SHA
 */
export async function getReviewByCommit(sha: string): Promise<CodeReview> {
  const response = await apiClient.get<ApiResponse<CodeReview>>(`/reviews/commit/${sha}`);
  return response.data.data as CodeReview;
}

/**
 * Trigger a manual review for a commit
 */
export async function triggerReview(commitSha: string, repoId: string): Promise<{ message: string; reviewId?: string }> {
  const response = await apiClient.post<ApiResponse<{ message: string; reviewId?: string }>>(
    `/reviews/trigger/${commitSha}`,
    { repoId }
  );
  return response.data.data as { message: string; reviewId?: string };
}
